import { useEffect, useMemo, useState } from "react";
import {
  US_STATES,
  changedTenantProfileFields,
  normalizePhone,
  normalizeTenantProfile,
  tenantProfileFormValues,
  validateTenantProfile,
} from "../utils/tenantProfile";

function FieldError({ id, message }) {
  if (!message) return null;
  return <span id={id} className="admin-field-error" role="alert">{message}</span>;
}

export default function TenantForm({
  tenant,
  mailboxes,
  onSubmit,
  onCancel,
  onDelete,
  isSubmitting = false,
  submitError = "",
  externalErrors = {},
  filesSection = null,
}) {
  const [form, setForm] = useState(() => tenantProfileFormValues(tenant));
  const [errors, setErrors] = useState({});
  const [mailboxQuery, setMailboxQuery] = useState("");
  const [selectedMailboxIds, setSelectedMailboxIds] = useState(
    () => tenant?.mailboxes?.map(({ id }) => id) || [],
  );

  useEffect(() => {
    setForm(tenantProfileFormValues(tenant));
    setErrors({});
    setMailboxQuery("");
    setSelectedMailboxIds(tenant?.mailboxes?.map(({ id }) => id) || []);
  }, [tenant]);

  const normalized = useMemo(() => normalizeTenantProfile(form), [form]);
  const mailboxSelectionEnabled = Array.isArray(mailboxes);
  const originalMailboxIds = useMemo(
    () => tenant?.mailboxes?.map(({ id }) => id).sort((left, right) => left - right) || [],
    [tenant],
  );
  const normalizedMailboxIds = useMemo(
    () => [...selectedMailboxIds].sort((left, right) => left - right),
    [selectedMailboxIds],
  );
  const mailboxSelectionChanged = mailboxSelectionEnabled
    && originalMailboxIds.join(",") !== normalizedMailboxIds.join(",");
  const hasChanges = !tenant
    || changedTenantProfileFields(tenant, normalized).length > 0
    || mailboxSelectionChanged;
  const displayedErrors = { ...externalErrors, ...errors };

  const mailboxChoices = useMemo(() => {
    if (!mailboxSelectionEnabled) return [];
    const choices = new Map((tenant?.mailboxes || []).map((mailbox) => [mailbox.id, mailbox]));
    mailboxes.forEach((mailbox) => {
      const assignedTenantId = mailbox.tenant?.id ?? mailbox.tenantId;
      if (!assignedTenantId || assignedTenantId === tenant?.id) choices.set(mailbox.id, mailbox);
    });
    return [...choices.values()].sort((left, right) => Number(left.number) - Number(right.number));
  }, [mailboxSelectionEnabled, mailboxes, tenant]);

  const visibleMailboxChoices = useMemo(() => {
    const query = mailboxQuery.trim().toLowerCase();
    if (!query) return mailboxChoices;
    return mailboxChoices.filter(({ number }) => String(number).toLowerCase().includes(query));
  }, [mailboxChoices, mailboxQuery]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: undefined, ...(name.endsWith("Phone") ? { phones: undefined } : {}) }));
  };

  const handlePhoneBlur = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: normalizePhone(value) || "" }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (typeof onSubmit !== "function") return;

    const validationErrors = validateTenantProfile(normalized);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    onSubmit(mailboxSelectionEnabled
      ? { ...normalized, mailboxIds: normalizedMailboxIds }
      : normalized);
  };

  const toggleMailbox = (mailboxId) => {
    setSelectedMailboxIds((current) => current.includes(mailboxId)
      ? current.filter((id) => id !== mailboxId)
      : [...current, mailboxId]);
  };

  return (
    <form className="admin-tenant-profile-form" onSubmit={handleSubmit} noValidate>
      <div className="admin-modal-body">
        {submitError && <p className="admin-empty-state admin-error-state" role="alert">{submitError}</p>}

        <fieldset className="admin-profile-sections admin-profile-fields" disabled={isSubmitting}>
          <fieldset className="admin-profile-section">
            <legend>Contact</legend>
            <div className="admin-form admin-profile-grid">
              <label>
                <span className="admin-field-label">First name <span className="admin-required-marker">Required</span></span>
                <input
                  name="firstName"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.firstName)}
                  aria-describedby={displayedErrors.firstName ? "tenant-first-name-error" : undefined}
                  autoFocus
                />
                <FieldError id="tenant-first-name-error" message={displayedErrors.firstName} />
              </label>
              <label>
                Middle name
                <input
                  name="middleName"
                  placeholder="Middle name"
                  value={form.middleName}
                  onChange={handleChange}
                />
              </label>
              <label>
                <span className="admin-field-label">Last name <span className="admin-required-marker">Required</span></span>
                <input
                  name="lastName"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.lastName)}
                  aria-describedby={displayedErrors.lastName ? "tenant-last-name-error" : undefined}
                />
                <FieldError id="tenant-last-name-error" message={displayedErrors.lastName} />
              </label>
              <label>
                <span className="admin-field-label">Email <span className="admin-required-marker">Required</span></span>
                <input
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  value={form.email}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.email)}
                  aria-describedby={displayedErrors.email ? "tenant-email-error" : undefined}
                />
                <FieldError id="tenant-email-error" message={displayedErrors.email} />
              </label>
              <label className="admin-field-wide">
                Legal business name
                <input name="businessName" placeholder="Legal company name" value={form.businessName} onChange={handleChange} />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-profile-section">
            <legend>Physical address</legend>
            <div className="admin-form admin-profile-grid">
              <label className="admin-field-wide">
                <span className="admin-field-label">Street address <span className="admin-required-marker">Required</span></span>
                <input
                  name="streetAddress"
                  placeholder="123 Main Street, Suite 200"
                  value={form.streetAddress}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.streetAddress)}
                  aria-describedby={displayedErrors.streetAddress ? "tenant-street-error" : undefined}
                />
                <FieldError id="tenant-street-error" message={displayedErrors.streetAddress} />
              </label>
              <label>
                <span className="admin-field-label">City <span className="admin-required-marker">Required</span></span>
                <input
                  name="city"
                  placeholder="City"
                  value={form.city}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.city)}
                  aria-describedby={displayedErrors.city ? "tenant-city-error" : undefined}
                />
                <FieldError id="tenant-city-error" message={displayedErrors.city} />
              </label>
              <label>
                <span className="admin-field-label">State <span className="admin-required-marker">Required</span></span>
                <select
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.state)}
                  aria-describedby={displayedErrors.state ? "tenant-state-error" : undefined}
                >
                  <option value="">Select a state</option>
                  {US_STATES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
                </select>
                <FieldError id="tenant-state-error" message={displayedErrors.state} />
              </label>
              <label>
                <span className="admin-field-label">ZIP code <span className="admin-required-marker">Required</span></span>
                <input
                  name="zipCode"
                  inputMode="numeric"
                  placeholder="60601 or 60601-1234"
                  value={form.zipCode}
                  onChange={handleChange}
                  aria-invalid={Boolean(displayedErrors.zipCode)}
                  aria-describedby={displayedErrors.zipCode ? "tenant-zip-error" : undefined}
                />
                <FieldError id="tenant-zip-error" message={displayedErrors.zipCode} />
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-profile-section">
            <legend>Phone numbers <span className="admin-required-marker">At least one required</span></legend>
            <div className="admin-form admin-profile-grid admin-profile-phone-grid">
              {[
                ["mobilePhone", "Mobile phone"],
                ["businessPhone", "Business phone"],
                ["homePhone", "Home phone"],
              ].map(([field, label]) => (
                <label key={field}>
                  {label}
                  <input
                    name={field}
                    type="tel"
                    placeholder="(312) 555-0123"
                    value={form[field]}
                    onChange={handleChange}
                    onBlur={handlePhoneBlur}
                    aria-invalid={Boolean(displayedErrors[field])}
                    aria-describedby={displayedErrors[field] ? `${field}-error` : undefined}
                  />
                  <FieldError id={`${field}-error`} message={displayedErrors[field]} />
                </label>
              ))}
            </div>
            <FieldError id="tenant-phones-error" message={displayedErrors.phones} />
          </fieldset>

          {mailboxSelectionEnabled && (
            <fieldset className="admin-profile-section admin-tenant-mailbox-picker">
              <legend>Mailbox assignment</legend>
              <div className="admin-tenant-mailbox-picker-header">
                <label className="admin-tenant-search">
                  Search available mailboxes
                  <input
                    type="search"
                    placeholder="Search by mailbox number"
                    value={mailboxQuery}
                    onChange={(event) => setMailboxQuery(event.target.value)}
                  />
                </label>
                <span>{normalizedMailboxIds.length} selected</span>
              </div>
              {visibleMailboxChoices.length ? (
                <div className="admin-available-mailbox-grid" role="group" aria-label="Available mailboxes">
                  {visibleMailboxChoices.map((mailbox) => {
                    const selected = selectedMailboxIds.includes(mailbox.id);
                    const current = originalMailboxIds.includes(mailbox.id);
                    return (
                      <label className={`admin-available-mailbox ${selected ? "is-selected" : ""}`.trim()} key={mailbox.id}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleMailbox(mailbox.id)}
                        />
                        <strong>#{mailbox.number}</strong>
                        <span>{current ? "Current assignment" : "Available"}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="admin-panel-empty">
                  {mailboxQuery ? "No available mailboxes match your search." : "No mailboxes are currently available."}
                </p>
              )}
            </fieldset>
          )}

          <fieldset className="admin-profile-section">
            <legend>Access</legend>
            <div className="admin-form admin-profile-grid">
              <label>
                Building access key number
                <input
                  name="buildingAccessKeyNumber"
                  placeholder="Key or access identifier"
                  value={form.buildingAccessKeyNumber}
                  onChange={handleChange}
                />
              </label>
            </div>
          </fieldset>
          {filesSection}
        </fieldset>
      </div>

      <div className="admin-modal-footer admin-profile-footer">
        <div>
          {tenant && (
            <button className="admin-button admin-button-danger" type="button" onClick={onDelete} disabled={isSubmitting}>
              Delete Tenant
            </button>
          )}
        </div>
        <div className="admin-form-actions">
          <button className="admin-button admin-button-secondary" type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="admin-button admin-button-primary" type="submit" disabled={isSubmitting || !hasChanges}>
            {isSubmitting ? "Saving..." : tenant ? "Save Changes" : "Create Tenant"}
          </button>
        </div>
      </div>
    </form>
  );
}
